// Asigna un formulario a una o más empresas (genera/reutiliza el link
// público por empresa) y, en un segundo paso, arma y envía el correo con
// ese link para cada empresa seleccionada.
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { X, Send, Loader, AlertCircle, CheckCircle } from "lucide-react";
import { api } from "../api";
import { parseEmailList } from "../utils/emails";

function defaultSubject(form) {
  return `[SST] Formulario: ${form.name}`;
}

function defaultBody(form, url) {
  return `Estimado(a),\n\nLe compartimos el siguiente formulario del Sistema de Gestión de Seguridad y Salud en el Trabajo (SG-SST): "${form.name}".\n\nPor favor ingrese al siguiente enlace para diligenciarlo:\n${url}\n\nSus datos personales serán tratados conforme a la Ley 1581 de 2012 de protección de datos.\n\nAtentamente,\nÁrea de Seguridad y Salud en el Trabajo`;
}

export default function FormAssignModal({ form, onClose }) {
  const [companies, setCompanies] = useState([]);
  const [links, setLinks]         = useState([]);
  const [selected, setSelected]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [step, setStep]           = useState("select"); // "select" | "send"
  const [rows, setRows]           = useState([]);
  const [error, setError]         = useState(null);

  useEffect(() => {
    Promise.all([api.getCompanies(), api.getFormLinks(form.id)]).then(([c, l]) => {
      setCompanies(Array.isArray(c) ? c : []);
      setLinks(Array.isArray(l) ? l : []);
      setLoading(false);
    });
  }, [form.id]);

  const toggle = (id) => setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const activeLinkFor = (companyId) => links.find(l => l.company_id === companyId && l.active);

  // Genera (o reutiliza) el link por cada empresa seleccionada y arma la
  // fila de envío de cada una (destinatarios vacíos, asunto/cuerpo por defecto).
  const handleContinue = async () => {
    if (selected.length === 0) return;
    setLoading(true);
    setError(null);
    const result = await api.assignForm(form.id, selected);
    setLoading(false);
    if (result.error) { setError(result.error); return; }

    const newRows = result.links.map(l => {
      const company = companies.find(c => c.id === l.company_id);
      const url = `${window.location.origin}/?public_form_token=${l.token}`;
      return {
        companyId: l.company_id,
        companyName: company?.name || "—",
        to: "",
        subject: defaultSubject(form),
        body: defaultBody(form, url),
        url,
        status: null, // null | "sending" | "ok" | "error"
        error: "",
      };
    });
    setRows(newRows);
    setStep("send");
  };

  const updateRow = (companyId, changes) => {
    setRows(prev => prev.map(r => r.companyId === companyId ? { ...r, ...changes } : r));
  };

  const sendRow = async (row) => {
    const { valid: to } = parseEmailList(row.to);
    if (!to.length) return;
    updateRow(row.companyId, { status: "sending" });
    try {
      const result = await api.sendFormLinkEmail(form.id, { company_id: row.companyId, to, subject: row.subject, body: row.body, url: row.url });
      if (result.error) throw new Error(result.error);
      if (result.failed?.length) {
        updateRow(row.companyId, { status: "partial", result });
      } else {
        updateRow(row.companyId, { status: "ok", result });
      }
    } catch (e) {
      updateRow(row.companyId, { status: "error", error: e.message });
    }
  };

  return (
    <motion.div className="modal-overlay"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <motion.div className="modal modal-large"
        initial={{ opacity: 0, scale: 0.92, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.94, y: 12 }}
        transition={{ type: "spring", stiffness: 380, damping: 30, mass: 0.7 }}>

        <div className="modal-header">
          <h2>Asignar "{form.name}"</h2>
          <button className="btn-icon" onClick={onClose}><X size={20} /></button>
        </div>

        {loading ? (
          <div style={{ padding: 60, display: "flex", justifyContent: "center" }}>
            <Loader size={28} className="spin" opacity={0.4} />
          </div>
        ) : step === "select" ? (
          <>
            <div className="modal-form" style={{ maxHeight: "60vh", overflowY: "auto" }}>
              <p style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>
                Selecciona las empresas a las que se enviará este formulario.
              </p>
              {companies.map(c => {
                const link = activeLinkFor(c.id);
                return (
                  <label key={c.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 4px", cursor: "pointer" }}>
                    <input type="checkbox" checked={selected.includes(c.id)} onChange={() => toggle(c.id)} />
                    <span style={{ flex: 1 }}>{c.name}</span>
                    {link && <span style={{ fontSize: "0.68rem", color: "var(--primary)" }}>Ya asignado</span>}
                  </label>
                );
              })}
              {companies.length === 0 && <p style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>No hay empresas creadas.</p>}
              {error && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", background: "#FEE2E2", borderRadius: 8, color: "#C53030", fontSize: "0.78rem" }}>
                  <AlertCircle size={15} /> {error}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={onClose}>Cancelar</button>
              <button className="btn-primary" onClick={handleContinue} disabled={selected.length === 0}>
                Continuar
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="modal-form" style={{ maxHeight: "60vh", overflowY: "auto" }}>
              {rows.map(row => {
                const { valid: rowEmails, invalid: rowInvalid } = parseEmailList(row.to);
                return (
                <div key={row.companyId} style={{ border: "1px solid var(--border)", borderRadius: 10, padding: 12, marginBottom: 10 }}>
                  <p style={{ fontWeight: 600, fontSize: "0.88rem", marginBottom: 8 }}>{row.companyName}</p>
                  <div className="form-row">
                    <label>Para *</label>
                    <textarea className="form-input" rows={2}
                      placeholder="correo@empresa.com&#10;Pega varios correos: uno por línea, o separados por coma/punto y coma"
                      value={row.to} onChange={e => updateRow(row.companyId, { to: e.target.value })} />
                    {row.to.trim() && (
                      <p style={{ fontSize: "0.7rem", color: rowInvalid.length ? "#C53030" : "var(--text-muted)", marginTop: 4 }}>
                        {rowEmails.length} destinatario{rowEmails.length !== 1 ? "s" : ""} detectado{rowEmails.length !== 1 ? "s" : ""}
                        {rowInvalid.length > 0 && ` · se ignoraron ${rowInvalid.length} no válido${rowInvalid.length !== 1 ? "s" : ""}: ${rowInvalid.join(", ")}`}
                      </p>
                    )}
                  </div>
                  <div className="form-row">
                    <label>Asunto</label>
                    <input className="form-input" value={row.subject}
                      onChange={e => updateRow(row.companyId, { subject: e.target.value })} />
                  </div>
                  <div className="form-row">
                    <label>Mensaje</label>
                    <textarea className="form-input form-textarea" rows={5} value={row.body}
                      onChange={e => updateRow(row.companyId, { body: e.target.value })} />
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <button className="btn-primary" onClick={() => sendRow(row)} disabled={!rowEmails.length || row.status === "sending"}>
                      {row.status === "sending" ? <><Loader size={14} className="spin" /> Enviando...</> : <><Send size={14} /> Enviar{rowEmails.length > 1 ? ` (${rowEmails.length})` : ""}</>}
                    </button>
                    {row.status === "ok" && (
                      <span style={{ color: "var(--primary)", fontSize: "0.8rem", display: "flex", alignItems: "center", gap: 4 }}>
                        <CheckCircle size={14} /> {row.result?.total > 1 ? `Enviado a ${row.result.sent}` : "Enviado"}
                      </span>
                    )}
                    {row.status === "partial" && (
                      <span style={{ color: "#D97706", fontSize: "0.78rem", display: "flex", alignItems: "center", gap: 4 }}>
                        <AlertCircle size={14} /> {row.result.sent}/{row.result.total} enviados · fallaron: {row.result.failed.map(f => f.to).join(", ")}
                      </span>
                    )}
                    {row.status === "error" && (
                      <span style={{ color: "#C53030", fontSize: "0.78rem", display: "flex", alignItems: "center", gap: 4 }}>
                        <AlertCircle size={14} /> {row.error}
                      </span>
                    )}
                  </div>
                </div>
              );})}
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={onClose}>Cerrar</button>
            </div>
          </>
        )}
      </motion.div>
    </motion.div>
  );
}
